import React from "react";

export interface SaleData {
  clientId: string;
  amount: number;
  paymentDate: Date | null;
  startDate: Date | null;
  endDate: Date | null;
  [key: string]: any;
}

export const StatBox = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <div
      key={label}
      className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-1"
    >
      <span className="text-gray-500 text-sm">{label}</span>
      <span className="text-2xl font-semibold">{value}</span>
    </div>
  );
};

interface StatsBoxProps {
  data: SaleData[];
}

const StatsBox: React.FC<StatsBoxProps> = ({ data }) => {
  const today = new Date();
  const currentYear = today.getFullYear();

  // Filter only paid sales up to today
  const paidSales = data.filter(
    (p) => p.paymentDate && p.paymentDate.getTime() <= today.getTime()
  );

  // Calculations
  const uniqueClients = new Set(paidSales.map((p) => p.clientId)).size;
  const totalRevenue = paidSales.reduce((sum, p) => sum + p.amount, 0);
  const ltvPerClient = uniqueClients > 0 ? totalRevenue / uniqueClients : 0;

  const clientProjectCounts = paidSales.reduce<Record<string, number>>(
    (acc, p) => {
      acc[p.clientId] = (acc[p.clientId] || 0) + 1;
      return acc;
    },
    {}
  );
  const recurringClientsCount = Object.values(clientProjectCounts).filter(
    (c) => c >= 2
  ).length;
  const percentageRecurringClients =
    uniqueClients > 0 ? (recurringClientsCount / uniqueClients) * 100 : 0;
  const averageRecurrence =
    uniqueClients > 0 ? paidSales.length / uniqueClients : 0;

  const projectsStartedThisYear = paidSales.filter(
    (p) => p.startDate?.getFullYear() === currentYear
  ).length;

  const completedProjectsCount = paidSales.filter((p) => p.endDate).length;
  const aov =
    completedProjectsCount > 0 ? totalRevenue / completedProjectsCount : 0;

  const avgDuration =
    paidSales.reduce((sum, p) => {
      if (p.startDate && p.endDate) {
        return (
          sum +
          (p.endDate.getTime() - p.startDate.getTime()) / (1000 * 60 * 60 * 24)
        );
      }
      return sum;
    }, 0) / (completedProjectsCount || 1);

  const avgPaymentDelay =
    paidSales.reduce((sum, p) => {
      if (p.endDate && p.paymentDate) {
        return (
          sum +
          (p.paymentDate.getTime() - p.endDate.getTime()) /
            (1000 * 60 * 60 * 24)
        );
      }
      return sum;
    }, 0) / (completedProjectsCount || 1);

  // Bundle into an array for rendering
  const stats = [
    { label: "Clients", value: uniqueClients },
    {
      label: "LTV par client",
      value: ltvPerClient.toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
      }),
    },
    {
      label: "Pourcentage de clients récurrents",
      value: `${percentageRecurringClients.toFixed(0)}%`,
    },
    { label: "Récurrence d’un client", value: averageRecurrence.toFixed(1) },
    { label: "Projets réalisés cette année", value: projectsStartedThisYear },
    {
      label: "AOV",
      value: aov.toLocaleString("fr-FR", {
        style: "currency",
        currency: "EUR",
      }),
    },
    {
      label: "Durée moyenne d’un projet",
      value: `${avgDuration.toFixed(0)} jours`,
    },
    {
      label: "Délai moyen de paiement",
      value: `${avgPaymentDelay.toFixed(0)} jours`,
    },
  ];

  return (
    // <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    //   {stats.map((stat) => (
    //     <div
    //       key={stat.label}
    //       className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-1"
    //     >
    //       <span className="text-gray-500 text-sm">{stat.label}</span>
    //       <span className="text-xl font-semibold">{stat.value}</span>
    //     </div>
    //   ))}
    // </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {stats.map((stat) => (
        <StatBox key={stat.label} label={stat.label} value={stat.value} />
      ))}
    </div>
  );
};

export default StatsBox;

interface BillingStatsProps {
  data: SaleData[];
}

export const BillingStats: React.FC<BillingStatsProps> = ({ data }) => {
  const quotesCount = data.filter(
    (p) =>
      typeof p["Étape de facturation"] === "string" &&
      p["Étape de facturation"].toLowerCase().includes("devis")
  ).length;
  const invoicesCount = data.filter(
    (p) =>
      typeof p["Étape de facturation"] === "string" &&
      (p["Étape de facturation"].toLowerCase().includes("facture") ||
        p["Étape de facturation"].toLowerCase().includes("paiement"))
  ).length;

  const stats = [
    { label: "Devis émis", value: quotesCount },
    { label: "Factures émises", value: invoicesCount },
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {stats.map((stat) => (
        <div
          key={stat.label}
          className="bg-white border border-gray-200 rounded-lg p-4 flex flex-col gap-1"
        >
          <span className="text-gray-500 text-sm">{stat.label}</span>
          <span className="text-xl font-semibold">{stat.value}</span>
        </div>
      ))}
    </div>
  );
};
